/**
 * Condition AST — parse / serialise the expression language the questionnaire uses.
 *
 * ## Why this exists
 *
 * Conditions are stored as raw JavaScript strings and evaluated with `new Function`
 * on the client and Nashorn on the server. That is editable only as text, and text is
 * why one of the three live defects exists at all:
 *
 *     ❌ (metric['Q_1_1_2_a1']||metric['Q_1_1_2_b1'] + metric['Q_1_1_2_a2']||…) >= 1000
 *     ✅ ((metric['Q_1_1_2_a1']||metric['Q_1_1_2_b1']) + (metric['Q_1_1_2_a2']||…)) >= 1000
 *
 * `+` binds tighter than `||`, so the first is a truthiness chain, not a sum. A regex
 * checker cannot see that; only a parser can. With a tree, parentheses come from the
 * STRUCTURE on the way out, so the mistake has no representable form.
 *
 * ## Scope: exactly the operators the live data uses, and no more
 *
 * Measured over all 54 expressions in `q_1_1…q_1_5` (51 distinct):
 *
 *     compare   === >= > <= <        34 / 9 / 11 / 1 / 3
 *     logic     && || !              13 / 4 / 1
 *     arith     + *                  8 / 4
 *     member    .includes(s)         12
 *     calls     hasKey(s)            2
 *               hasAnyMatch(s, [s…]) 4
 *
 * Deliberately NOT supported: `!==`, `-`, `/`, ternaries, regexes, property access
 * beyond `metric[...]`, object literals. None appear. Keeping the grammar this small
 * is what makes a hand-written parser the right tool rather than a JS parser
 * dependency — and it means an expression this cannot parse is a RED FLAG worth
 * surfacing, not a gap to paper over.
 */
import type { QuestionnaireDefinition } from '../types';

export type CompareOp = '===' | '>=' | '>' | '<=' | '<';

export type Expr =
  /** A question's answer: `metric['Q_1_1_1']`. `ref` is the `qualifiedNodeId`. */
  | { k: 'var'; ref: string }
  | { k: 'lit'; v: string | number }
  | { k: 'cmp'; op: CompareOp; l: Expr; r: Expr }
  /**
   * Flat, not binary.
   *
   * `a && b && c` has two binary shapes and only one flat one, so flattening gives a
   * condition exactly ONE representation — which is what makes a draft-vs-live diff of
   * conditions meaningful. It is also what a drag-and-drop editor wants: reordering is
   * a splice, not a sequence of rotations.
   */
  | { k: 'and'; xs: Expr[] }
  | { k: 'or'; xs: Expr[] }
  | { k: 'not'; x: Expr }
  | { k: 'add'; xs: Expr[] }
  | { k: 'mul'; xs: Expr[] }
  /** `x.includes(v)` — membership in a multi-select answer. */
  | { k: 'has'; x: Expr; v: string }
  /** `hasKey('Program Name')` — was this program already granted. */
  | { k: 'hasKey'; program: string }
  /**
   * `hasAnyMatch('Q_1_1_3_1', ['a','b',…])`.
   *
   * Note the first argument is the question name as a STRING, not `metric[...]` — two
   * referencing styles in one language. Faithful to the data, but every traversal has
   * to count `ref` as a reference or dominance checks go blind on it. `metricRefsOf`
   * in projection.ts currently does exactly that, and misses these four expressions.
   */
  | { k: 'anyMatch'; ref: string; vs: string[] }
  /**
   * `a || b` used as "whichever of these was actually asked" — NOT a logical or.
   *
   * Kept separate from `or` on purpose. The live `(a1||b1) + (a2||b2)` is arithmetic
   * over two fallback chains; treating it as a boolean `or` loses the distinction that
   * decides whether the serialiser must parenthesise, which is the precedence defect
   * above. One node type per meaning.
   */
  | { k: 'coalesce'; xs: Expr[] };

/* ── Serialise ──────────────────────────────────────────────────────────────── */

/**
 * Binding power, loosest first. Used only to decide parentheses, so it mirrors
 * JavaScript's own precedence rather than inventing one.
 */
const PREC: Record<Expr['k'], number> = {
  or: 1,
  coalesce: 1,
  and: 2,
  cmp: 3,
  add: 4,
  mul: 5,
  not: 6,
  var: 9,
  lit: 9,
  has: 9,
  hasKey: 9,
  anyMatch: 9,
};

const quote = (s: string) => `'${s.replace(/'/g, "\\'")}'`;

/** Wrap `child` when its own binding power is looser than the slot it sits in. */
function sub(child: Expr, minPrec: number): string {
  const text = serialize(child);
  return PREC[child.k] < minPrec ? `(${text})` : text;
}

export function serialize(e: Expr): string {
  switch (e.k) {
    case 'var':
      return `metric[${quote(e.ref)}]`;
    case 'lit':
      return typeof e.v === 'number' ? String(e.v) : quote(e.v);
    case 'cmp':
      return `${sub(e.l, PREC.cmp + 1)} ${e.op} ${sub(e.r, PREC.cmp + 1)}`;
    case 'and':
      return e.xs.map((x) => sub(x, PREC.and)).join(' && ');
    case 'or':
      return e.xs.map((x) => sub(x, PREC.or + 1)).join(' || ');
    case 'coalesce':
      return e.xs.map((x) => sub(x, PREC.coalesce + 1)).join(' || ');
    case 'not':
      return `!${sub(e.x, PREC.not)}`;
    case 'add':
      return e.xs.map((x) => sub(x, PREC.add)).join(' + ');
    case 'mul':
      return e.xs.map((x) => sub(x, PREC.mul)).join(' * ');
    case 'has':
      return `${sub(e.x, PREC.has)}.includes(${quote(e.v)})`;
    case 'hasKey':
      return `hasKey(${quote(e.program)})`;
    case 'anyMatch':
      return `hasAnyMatch(${quote(e.ref)}, [${e.vs.map(quote).join(', ')}])`;
  }
}

/* ── Parse ──────────────────────────────────────────────────────────────────── */

export class ParseError extends Error {
  constructor(
    message: string,
    /** Offset into the source where parsing stopped. */
    readonly at: number,
  ) {
    super(message);
    this.name = 'ParseError';
  }
}

interface Cursor {
  s: string;
  i: number;
}

const ws = (c: Cursor) => {
  while (c.i < c.s.length && /\s/.test(c.s[c.i])) c.i++;
};

function eat(c: Cursor, token: string): boolean {
  ws(c);
  if (c.s.startsWith(token, c.i)) {
    c.i += token.length;
    return true;
  }
  return false;
}

function expect(c: Cursor, token: string): void {
  if (!eat(c, token)) throw new ParseError(`expected ${token}`, c.i);
}

function parseString(c: Cursor): string {
  ws(c);
  const q = c.s[c.i];
  if (q !== "'" && q !== '"') throw new ParseError('expected a string', c.i);
  c.i++;
  let out = '';
  while (c.i < c.s.length && c.s[c.i] !== q) {
    if (c.s[c.i] === '\\') c.i++;
    out += c.s[c.i++];
  }
  if (c.s[c.i] !== q) throw new ParseError('unterminated string', c.i);
  c.i++;
  return out;
}

/**
 * Build an associative node with same-kind children spliced in.
 *
 * `&&`, `||`, `+` and `*` are all associative, so `(a && b) && c` and `a && (b && c)`
 * denote the same thing — and explicit parentheses in the source would otherwise
 * produce two different trees for it. Normalising here is what makes the "one
 * condition, one representation" claim true, which is what a draft-vs-live diff of
 * conditions and a splice-based drag editor both rest on.
 */
function assoc(k: 'and' | 'or' | 'add' | 'mul' | 'coalesce', xs: Expr[]): Expr {
  const flat: Expr[] = [];
  for (const x of xs) {
    if (x.k === k) flat.push(...x.xs);
    else flat.push(x);
  }
  return flat.length === 1 ? flat[0] : ({ k, xs: flat } as Expr);
}

/*
 * Precedence climbing, loosest level first. `||` is handled at one level and resolved
 * into `or` vs `coalesce` afterwards — see `disambiguateOr`, because the two are
 * spelled identically in the source and only the operand types tell them apart.
 */
function parseOr(c: Cursor): Expr {
  const xs = [parseAnd(c)];
  while (eat(c, '||')) xs.push(parseAnd(c));
  if (xs.length === 1) return xs[0];
  return disambiguateOr(xs);
}

/**
 * `a || b` is a fallback chain when its operands are plain values, and a logical or
 * when they are conditions.
 *
 * The live data only ever uses it as a fallback (`(a1||b1) + (a2||b2)`), but an author
 * can legitimately write `x === 'a' || y === 'b'`, so the distinction is made from the
 * operands rather than assumed.
 */
function disambiguateOr(xs: Expr[]): Expr {
  const boolish = new Set<Expr['k']>(['cmp', 'and', 'or', 'not', 'has', 'hasKey', 'anyMatch']);
  return xs.every((x) => !boolish.has(x.k)) ? assoc('coalesce', xs) : assoc('or', xs);
}

function parseAnd(c: Cursor): Expr {
  const xs = [parseCmp(c)];
  while (eat(c, '&&')) xs.push(parseCmp(c));
  return xs.length === 1 ? xs[0] : assoc('and', xs);
}

/** Longest first, so `===` is not mistaken for `==` and `>=` not for `>`. */
const CMP_OPS: CompareOp[] = ['===', '>=', '<=', '>', '<'];

function parseCmp(c: Cursor): Expr {
  const l = parseAdd(c);
  ws(c);
  // `==` is accepted as a spelling of `===`; the live files use `===` throughout, but
  // nothing stops an author from typing two.
  for (const op of CMP_OPS) {
    if (eat(c, op)) return { k: 'cmp', op, l, r: parseAdd(c) };
  }
  if (eat(c, '==')) return { k: 'cmp', op: '===', l, r: parseAdd(c) };
  return l;
}

function parseAdd(c: Cursor): Expr {
  const xs = [parseMul(c)];
  while (eat(c, '+')) xs.push(parseMul(c));
  return xs.length === 1 ? xs[0] : assoc('add', xs);
}

function parseMul(c: Cursor): Expr {
  const xs = [parseUnary(c)];
  while (eat(c, '*')) xs.push(parseUnary(c));
  return xs.length === 1 ? xs[0] : assoc('mul', xs);
}

function parseUnary(c: Cursor): Expr {
  if (eat(c, '!')) return { k: 'not', x: parseUnary(c) };
  return parsePostfix(c);
}

function parsePostfix(c: Cursor): Expr {
  let x = parseAtom(c);
  for (;;) {
    const save = c.i;
    if (eat(c, '.includes(')) {
      const v = parseString(c);
      expect(c, ')');
      x = { k: 'has', x, v };
      continue;
    }
    c.i = save;
    return x;
  }
}

function parseAtom(c: Cursor): Expr {
  ws(c);
  if (eat(c, '(')) {
    const x = parseOr(c);
    expect(c, ')');
    return x;
  }
  if (eat(c, 'metric[')) {
    const ref = parseString(c);
    expect(c, ']');
    return { k: 'var', ref };
  }
  if (eat(c, 'hasKey(')) {
    const program = parseString(c);
    expect(c, ')');
    return { k: 'hasKey', program };
  }
  if (eat(c, 'hasAnyMatch(')) {
    const ref = parseString(c);
    expect(c, ',');
    expect(c, '[');
    const vs: string[] = [];
    if (!eat(c, ']')) {
      do {
        vs.push(parseString(c));
      } while (eat(c, ','));
      expect(c, ']');
    }
    expect(c, ')');
    return { k: 'anyMatch', ref, vs };
  }
  if (eat(c, 'true')) return { k: 'lit', v: 'true' };
  const num = /^-?\d+(\.\d+)?/.exec(c.s.slice(c.i));
  if (num) {
    c.i += num[0].length;
    return { k: 'lit', v: Number(num[0]) };
  }
  if (c.s[c.i] === "'" || c.s[c.i] === '"') return { k: 'lit', v: parseString(c) };
  throw new ParseError(`cannot parse from here: ${JSON.stringify(c.s.slice(c.i, c.i + 24))}`, c.i);
}

/** Parse an expression, or throw `ParseError`. */
export function parse(src: string): Expr {
  const c: Cursor = { s: src, i: 0 };
  const x = parseOr(c);
  ws(c);
  if (c.i !== c.s.length) throw new ParseError('trailing input', c.i);
  return x;
}

/** Parse without throwing — `null` when the expression is outside the grammar. */
export function tryParse(src: string): Expr | null {
  try {
    return parse(src);
  } catch {
    return null;
  }
}

/* ── Traversal ──────────────────────────────────────────────────────────────── */

/** Every sub-expression, parents before children. */
export function walk(e: Expr): Expr[] {
  const out: Expr[] = [e];
  switch (e.k) {
    case 'cmp':
      out.push(...walk(e.l), ...walk(e.r));
      break;
    case 'and':
    case 'or':
    case 'add':
    case 'mul':
    case 'coalesce':
      for (const x of e.xs) out.push(...walk(x));
      break;
    case 'not':
      out.push(...walk(e.x));
      break;
    case 'has':
      out.push(...walk(e.x));
      break;
    default:
      break;
  }
  return out;
}

/**
 * Every question this expression reads.
 *
 * Counts `anyMatch.ref` as well as `var.ref` — the whole point of having a tree. The
 * regex-based `metricRefsOf` in projection.ts sees only `metric['X']`, so it is blind
 * to the four live `hasAnyMatch` expressions and reports no dominance findings for
 * them.
 */
export function refsOf(e: Expr): string[] {
  const out = new Set<string>();
  for (const n of walk(e)) {
    if (n.k === 'var') out.add(n.ref);
    if (n.k === 'anyMatch') out.add(n.ref);
  }
  return [...out];
}

/** Every program name the expression probes with `hasKey`. */
export function programsOf(e: Expr): string[] {
  return [...new Set(walk(e).flatMap((n) => (n.k === 'hasKey' ? [n.program] : [])))];
}

/** Structural equality. The right correctness test for a serialiser: text may differ
 *  in whitespace or redundant parentheses and still mean exactly the same thing. */
export function exprEqual(a: Expr, b: Expr): boolean {
  if (a.k !== b.k) return false;
  switch (a.k) {
    case 'var':
      return a.ref === (b as typeof a).ref;
    case 'lit':
      return a.v === (b as typeof a).v;
    case 'cmp': {
      const o = b as typeof a;
      return a.op === o.op && exprEqual(a.l, o.l) && exprEqual(a.r, o.r);
    }
    case 'and':
    case 'or':
    case 'add':
    case 'mul':
    case 'coalesce': {
      const o = b as typeof a;
      return a.xs.length === o.xs.length && a.xs.every((x, i) => exprEqual(x, o.xs[i]));
    }
    case 'not':
      return exprEqual(a.x, (b as typeof a).x);
    case 'has': {
      const o = b as typeof a;
      return a.v === o.v && exprEqual(a.x, o.x);
    }
    case 'hasKey':
      return a.program === (b as typeof a).program;
    case 'anyMatch': {
      const o = b as typeof a;
      return a.ref === o.ref && a.vs.length === o.vs.length && a.vs.every((v, i) => v === o.vs[i]);
    }
  }
}

/**
 * A fallback chain with arithmetic inside it — the signature of a missing parenthesis.
 *
 * `(a1 || b1 + a2 || b2)` parses as `a1 || (b1 + a2) || b2`: a truthiness chain, not a
 * sum, because `+` binds tighter than `||`. Nobody writes that on purpose — the intent
 * is always `(a1 || b1) + (a2 || b2)`. The live `Q1.1.4.1` rule is exactly this, and its
 * own sibling `Q1.1.6` has the parentheses, which is what makes the intent unambiguous.
 *
 * This is the check that needs a tree: no regex over the text can tell the two apart.
 */
export function suspiciousCoalesce(e: Expr): boolean {
  return walk(e).some(
    (n) => n.k === 'coalesce' && n.xs.some((x) => x.k === 'add' || x.k === 'mul'),
  );
}

/* ── Round-trip audit ───────────────────────────────────────────────────────── */

export interface AuditRow {
  nodeId: string;
  /** Which rule family it came from. */
  kind: 'next' | 'program' | 'auto';
  index: number;
  source: string;
  /** Re-serialised from the parse tree. */
  roundTrip: string | null;
  /** Set when the grammar could not cover it. */
  error?: string;
  /**
   * Does the round-trip mean the same thing?
   *
   * Compared as TREES, not text: dropping a redundant `(a + b) >= 300` paren or
   * normalising `==='a'` to `=== 'a'` changes the string and not the meaning. Only a
   * structural difference would be a serialiser bug.
   */
  faithful: boolean;
  /** True when the text changed at all — cosmetic when `faithful` is also true. */
  reformatted: boolean;
  /** `suspiciousCoalesce` — a fallback chain containing arithmetic. */
  suspicious: boolean;
}

const norm = (s: string) => s.replace(/\s+/g, ' ').replace(/\s*([()[\],])\s*/g, '$1').trim();

/**
 * Parse and re-serialise every expression in a definition.
 *
 * This is the double-run from the migration plan: before any editor writes through the
 * AST, the AST has to demonstrably represent what is already stored.
 */
export function auditDefinition(def: QuestionnaireDefinition): AuditRow[] {
  const rows: AuditRow[] = [];
  const add = (nodeId: string, kind: AuditRow['kind'], index: number, source: string) => {
    const src = source.trim();
    try {
      const tree = parse(src);
      const rt = serialize(tree);
      rows.push({
        nodeId,
        kind,
        index,
        source: src,
        roundTrip: rt,
        faithful: exprEqual(tree, parse(rt)),
        reformatted: norm(rt) !== norm(src),
        suspicious: suspiciousCoalesce(tree),
      });
    } catch (err) {
      rows.push({
        nodeId,
        kind,
        index,
        source: src,
        roundTrip: null,
        error: err instanceof Error ? err.message : String(err),
        faithful: false,
        reformatted: true,
        suspicious: false,
      });
    }
  };
  for (const n of def.nodes) {
    (n.nextQuestionRules ?? []).forEach((r, i) => add(n.nodeId, 'next', i, r.expression));
    (n.programRules ?? []).forEach((r, i) => add(n.nodeId, 'program', i, r.expression));
    (n.autoAnswerRules ?? []).forEach((r, i) => add(n.nodeId, 'auto', i, r.expression));
  }
  return rows;
}
